import React, { useState, useEffect } from 'react';
import alertService from '../../services/AlertService.js';
import Alert from './Alert.js';

const AlertModal = ({ isOpen, onClose, userId }) => {
  const [activeTab, setActiveTab] = useState('unread');
  const [unreadAlerts, setUnreadAlerts] = useState([]);
  const [readAlerts, setReadAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && userId) {
      fetchAlerts();
    }
  }, [isOpen, userId, activeTab]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      if (activeTab === 'unread') {
        const response = await alertService.getUnread(userId);
        setUnreadAlerts(response.data || []);
      } else {
        const response = await alertService.getRead(userId);
        setReadAlerts(response.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      Alert.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    const confirmed = await Alert.confirm(
      'Mark all unread alerts as read?',
      'Confirm Action'
    );
    if (confirmed) {
      try {
        await alertService.markAllAsRead(userId);
        Alert.success('All alerts marked as read');
        fetchAlerts();
      } catch (err) {
        Alert.error('Failed to mark alerts as read');
      }
    }
  };

  const handleClearAllUnread = async () => {
    const confirmed = await Alert.confirm(
      'Delete all unread alerts permanently?',
      'Confirm Delete'
    );
    if (confirmed) {
      try {
        await alertService.deleteAllUnread(userId);
        Alert.success('All unread alerts deleted');
        fetchAlerts();
      } catch (err) {
        Alert.error('Failed to delete unread alerts');
      }
    }
  };

  const handleClearAllRead = async () => {
    const confirmed = await Alert.confirm(
      'Delete all read alerts permanently?',
      'Confirm Delete'
    );
    if (confirmed) {
      try {
        await alertService.deleteAllRead(userId);
        Alert.success('All read alerts deleted');
        fetchAlerts();
      } catch (err) {
        Alert.error('Failed to delete read alerts');
      }
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await alertService.deleteAlert(alertId);
      Alert.success('Alert deleted');
      fetchAlerts();
    } catch (err) {
      Alert.error('Failed to delete alert');
    }
  };

  const handleMarkAsRead = async (alertId) => {
    try {
      await alertService.markAsRead(alertId);
      fetchAlerts();
    } catch (err) {
      Alert.error('Failed to mark alert as read');
    }
  };

  const getAlertTypeColor = (type) => {
    const colors = {
      info: 'bg-brand-light border-blue-200 text-blue-800',
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      error: 'bg-red-50 border-red-200 text-red-800',
      success: 'bg-green-50 border-green-200 text-green-800'
    };
    return colors[type] || colors.info;
  };

  const getAlertTypeIcon = (type) => {
    const icons = {
      info: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      ),
      warning: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      error: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      ),
      success: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      )
    };
    return icons[type] || icons.info;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  if (!isOpen) return null;

  const currentAlerts = activeTab === 'unread' ? unreadAlerts : readAlerts;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div 
        className="bg-white w-full sm:max-w-2xl sm:rounded-lg shadow-xl h-[85vh] sm:h-auto sm:max-h-[90vh] flex flex-col rounded-t-3xl sm:rounded-b-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile Pull Indicator */}
        <div className="flex sm:hidden justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b sticky top-0 bg-white z-10 rounded-t-3xl sm:rounded-t-lg">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">Notifications</h2>
          <button
            onClick={onClose}
            className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b bg-white sticky top-[53px] sm:top-[57px] z-10">
          <button
            onClick={() => setActiveTab('unread')}
            className={`flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium transition-colors touch-manipulation ${
              activeTab === 'unread'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <span className="hidden sm:inline">Unread</span>
            <span className="sm:hidden">Unread</span>
            {unreadAlerts.length > 0 && (
              <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold rounded-full ${
                activeTab === 'unread' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-700'
              }`}>
                {unreadAlerts.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('read')}
            className={`flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium transition-colors touch-manipulation ${
              activeTab === 'read'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <span className="hidden sm:inline">Read</span>
            <span className="sm:hidden">Read</span>
            {readAlerts.length > 0 && (
              <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold rounded-full ${
                activeTab === 'read' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-700'
              }`}>
                {readAlerts.length}
              </span>
            )}
          </button>
        </div>

        {/* Action Buttons */}
        {currentAlerts.length > 0 && (
          <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 border-b">
            {activeTab === 'unread' ? (
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button
                  onClick={handleMarkAllAsRead}
                  className="w-full sm:w-auto px-3 py-2 sm:py-1.5 text-xs font-medium text-brand bg-brand-light hover:bg-blue-100 rounded transition-colors touch-manipulation active:bg-blue-200"
                >
                  <span className="flex items-center justify-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Mark All as Read
                  </span>
                </button>
                <button
                  onClick={handleClearAllUnread}
                  className="w-full sm:w-auto px-3 py-2 sm:py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors touch-manipulation active:bg-red-200"
                >
                  <span className="flex items-center justify-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Clear All Unread
                  </span>
                </button>
              </div>
            ) : (
              <button
                onClick={handleClearAllRead}
                className="w-full sm:w-auto sm:ml-auto block px-3 py-2 sm:py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors touch-manipulation active:bg-red-200"
              >
                <span className="flex items-center justify-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Clear All Read
                </span>
              </button>
            )}
          </div>
        )}

        {/* Alert List */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {loading ? (
            <div className="flex items-center justify-center py-16 sm:py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-brand mx-auto mb-4"></div>
                <p className="text-gray-600 text-xs sm:text-sm">Loading notifications...</p>
              </div>
            </div>
          ) : currentAlerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 sm:py-20 px-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <p className="text-gray-900 text-sm sm:text-base font-semibold mb-1">No {activeTab} alerts</p>
              <p className="text-gray-500 text-xs sm:text-sm">You're all caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {currentAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`p-3 sm:p-4 hover:bg-gray-50 transition-colors active:bg-gray-100 ${
                    activeTab === 'unread' ? 'bg-brand-light/30' : ''
                  }`}
                >
                  <div className="flex gap-2.5 sm:gap-3">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${getAlertTypeColor(alert.alert_type)}`}>
                      {getAlertTypeIcon(alert.alert_type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1.5 sm:mb-2">
                        <p className="text-xs sm:text-sm text-gray-900 break-words leading-relaxed pr-2">
                          {alert.message}
                        </p>
                        <button
                          onClick={() => handleDeleteAlert(alert.id)}
                          className="flex-shrink-0 p-1.5 sm:p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors touch-manipulation active:bg-red-100"
                          title="Delete alert"
                        >
                          <svg className="w-4 h-4 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-500">
                        <span className="font-medium">{formatDate(alert.created_at)}</span>
                        {alert.reference_type && (
                          <>
                            <span className="hidden sm:inline">•</span>
                            <span className="capitalize bg-gray-100 px-2 py-0.5 rounded-full text-[10px]">
                              {alert.reference_type}
                            </span>
                          </>
                        )}
                        {activeTab === 'unread' && (
                          <>
                            <span className="hidden sm:inline">•</span>
                            <button
                              onClick={() => handleMarkAsRead(alert.id)}
                              className="text-brand hover:text-brand font-medium hover:underline touch-manipulation active:text-blue-800"
                            >
                              Mark as read
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mobile Bottom Safe Area */}
        <div className="h-safe-area-inset-bottom sm:hidden bg-white"></div>
      </div>
    </div>
  );
};

export default AlertModal;